/**
 * Custom hook for handling unit action keyboard shortcuts.
 */

import { useEffect } from 'react';
import { useUnitSelectionStore, useCommandModeStore } from '@shared/stores';
import { useBuildModeStore } from '@shared/stores/buildModeStore';

interface UseUnitActionKeyboardShortcutsOptions {
  scene: unknown | null;
}

/**
 * Hook for handling keyboard shortcuts for unit actions.
 */
export function useUnitActionKeyboardShortcuts({ scene }: UseUnitActionKeyboardShortcutsOptions) {
  const selectedUnitIds = useUnitSelectionStore((state) => state.selectedUnitIds);
  const { setMode: setCommandMode } = useCommandModeStore();
  const { isBuildMenuOpen, isTowerSubmenuOpen } = useBuildModeStore();

  useEffect(() => {
    // Only handle shortcuts if units are selected
    if (selectedUnitIds.length === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle if not typing in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (!scene || typeof scene !== 'object' || scene === null) return;

      const sceneWithMethods = scene as {
        getUnitCommandHandler?: () => {
          executeStopCommand?: (units: unknown[]) => void;
          executeHoldPositionCommand?: (units: unknown[]) => void;
        } | null;
        getUnitManager?: () => {
          getUnit: (id: string) => unknown | undefined;
        } | null;
      };

      const commandHandler = sceneWithMethods.getUnitCommandHandler?.();
      const unitManager = sceneWithMethods.getUnitManager?.();
      if (!commandHandler || !unitManager) return;

      const units = selectedUnitIds
        .map((id) => unitManager.getUnit(id))
        .filter((unit) => unit !== undefined);

      // 'S' key for Stop
      if (event.key === 's' || event.key === 'S') {
        event.preventDefault();
        if (commandHandler.executeStopCommand && units.length > 0) {
          commandHandler.executeStopCommand(units);
        }
      }

      // 'H' key for Hold Position
      if (event.key === 'h' || event.key === 'H') {
        event.preventDefault();
        if (commandHandler.executeHoldPositionCommand && units.length > 0) {
          commandHandler.executeHoldPositionCommand(units);
        }
      }

      // If the build menu / tower submenu is open, don't handle overlapping hotkeys
      const isBuildMenuBlocking = isBuildMenuOpen || isTowerSubmenuOpen;

      // 'M' key for Move mode
      if ((event.key === 'm' || event.key === 'M') && !isBuildMenuBlocking) {
        event.preventDefault();
        setCommandMode('move');
      }

      // 'A' key for Attack Move mode
      if ((event.key === 'a' || event.key === 'A') && !isBuildMenuBlocking) {
        event.preventDefault();
        setCommandMode('attack-move');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [scene, selectedUnitIds, setCommandMode, isBuildMenuOpen, isTowerSubmenuOpen]);
}
